
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/authContext";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Footer } from "@/components/Footer";

export default function ConfirmedPage() {
    const [, setLocation] = useLocation();
    const { isLoggedIn, refreshAuth } = useAuth();
    const { t } = useI18n();
    const { toast } = useToast();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Redirect to account if already logged in
    useEffect(() => {
        if (isLoggedIn) {
            setTimeout(() => setLocation("/account"), 2000);
        }
    }, [isLoggedIn, setLocation]);

    const handleLogin = async () => {
        setLoading(true);
        setError(null);

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            if (data.session) {
                await refreshAuth?.();
                toast({
                    title: "Ingelogd!",
                    description: "Je wordt nu doorgestuurd...",
                });
            }
        } catch (err: any) {
            setError(err.message || t("loginModal.loginFailed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <main className="flex-1 container mx-auto px-4 py-16 flex flex-col items-center justify-center">
                <div className="w-full max-w-md space-y-8">
                    <div className="text-center space-y-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="h-10 w-10 text-green-600" />
                        </div>
                        <h1 className="text-3xl font-bold tracking-tight text-foreground">
                            {t("emailConfirmed.title")}
                        </h1>
                        <p className="text-muted-foreground text-lg">
                            {t("emailConfirmed.desc")}
                        </p>
                    </div>

                    <Card className="border-primary/10 shadow-lg">
                        <CardHeader>
                            <CardTitle>{t("emailConfirmed.loginTitle")}</CardTitle>
                            <CardDescription>
                                {t("loginModal.haveAccountLogin")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Input
                                        type="email"
                                        placeholder={t("loginModal.email")}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={loading || isLoggedIn}
                                    />
                                    <div className="relative">
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            placeholder={t("loginModal.password")}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            disabled={loading || isLoggedIn}
                                            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                                            className="pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                            disabled={loading || isLoggedIn}
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>

                                {error && (
                                    <p className="text-sm text-destructive text-center">{error}</p>
                                )}

                                <Button
                                    size="lg"
                                    className="w-full"
                                    onClick={handleLogin}
                                    disabled={loading || !email || !password || isLoggedIn}
                                >
                                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    {isLoggedIn ? "Opgeslagen..." : t("nav.login")}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
            <Footer />
        </div>
    );
}
